// src/pages/TravelNews.jsx
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import api from "../utils/api";
import LiveStrip from "../components/LiveStrip";
import "../styles/travelNews.css";

const categories = [
  { key: "road", title: "Roads & Motorways", icon: "🛣️" },
  { key: "train", title: "Train & Tube", icon: "🚆" },
  { key: "airport", title: "Airport Notices", icon: "🛫" },
  { key: "local", title: "Local Travel Updates", icon: "📍" },
  { key: "weather", title: "Weather Impact", icon: "🌧️" },
];

export default function TravelNews() {
  const [data, setData] = useState({
    road: [],
    train: [],
    airport: [],
    local: [],
    weather: [],
  });
  const [open, setOpen] = useState({
    road: true,
    train: false,
    airport: false,
    local: false,
    weather: false,
  });
  const [loading, setLoading] = useState(true);

  // -------------------------------
  // LOAD TRAVEL NEWS FROM BACKEND
  // -------------------------------
  useEffect(() => {
    api
      .get("/api/travelnews")
      .then((res) => {
        const raw = res.data || {};

        // Backend returns grouped version
        if (!raw.items) {
          setData({
            road: raw.road || [],
            train: raw.train || [],
            airport: raw.airport || [],
            local: raw.local || [],
            weather: raw.weather || [],
          });
          setLoading(false);
          return;
        }

        // Backend returned array → group it
        const grouped = {
          road: [],
          train: [],
          airport: [],
          local: [],
          weather: [],
        };

        raw.items.forEach((item) => {
          const key = String(item.category || "").toLowerCase();
          if (grouped[key]) grouped[key].push(item);
        });

        setData(grouped);
        setLoading(false);
      })
      .catch((err) => {
        console.error("TravelNews fetch error", err);
        setLoading(false);
      });
  }, []);

  // -------------------------------
  // RENDER A SECTION
  // -------------------------------
  const renderSection = (key, icon, title) => {
    const list = data[key] || [];
    const isOpen = open[key];

    return (
      <section className="tn-section" id={`tn-${key}`} key={key}>
        {/* ---------- HEADER ---------- */}
        <button
          className="tn-accordion"
          onClick={() => setOpen({ ...open, [key]: !isOpen })}
          aria-expanded={isOpen}
        >
          <div className="tn-acc-left">
            <span className="tn-acc-icon">{icon}</span>
            <span>{title}</span>
          </div>
          <span className="tn-acc-arrow">{isOpen ? "▲" : "▼"}</span>
        </button>

        {/* ---------- CONTENT ---------- */}
        {isOpen && (
          <ul className="tn-list">
            {list.map((item) => {
              const time =
                item.start_time && item.end_time
                  ? `⏱ ${new Date(item.start_time).toLocaleTimeString("en-GB", {
                      hour: "2-digit",
                      minute: "2-digit",
                    })} → ${new Date(item.end_time).toLocaleTimeString(
                      "en-GB",
                      {
                        hour: "2-digit",
                        minute: "2-digit",
                      }
                    )}`
                  : "";

              return (



<li key={`${key}-${item.id}`} className="tn-row">
  <div className="tn-title">{item.title}</div>

  {item.description && (
    <div className="tn-desc">{item.description}</div>
  )}

  <div className="tn-meta">
    <span className="tn-source">{item.source || "Update"}</span>
    <span className="tn-dot">•</span>
    <span className="tn-updated">
      Updated{" "}
      {new Date(item.created_at).toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
      })}
    </span>
  </div>

  {time && <div className="tn-time">{time}</div>}


</li>



              );
            })}
          </ul>
        )}
      </section>
    );
  };

  // -------------------------------
  // MAIN RENDER
  // -------------------------------
  return (
    <div className="tn-wrapper">
      {/* ==============================
          SEO META DATA + JSON-LD
      =============================== */}
      <Helmet>
        <title>
          Live Travel News – Roads, Train, Airport & Local Updates | CheapCabGrab
        </title>
        <meta
          name="description"
          content="Live travel updates for M25, M4, Heathrow, TfL, GWR and Berkshire. Road closures, lane restrictions, airport notices and local traffic alerts. Updated regularly by CheapCabGrab."
        />
        <meta
          name="keywords"
          content="M25 traffic, M4 delays, Heathrow travel news, Slough traffic, Windsor traffic, Berkshire travel updates, TfL delays, CheapCabGrab travel"
        />
        <link rel="canonical" href="https://www.cheapcabgrab.com/travelnews" />
        <meta property="og:title" content="Live Travel News & Updates" />
        <meta
          property="og:description"
          content="Live road, rail, airport and local travel disruption updates for Slough, Windsor, Heathrow and London."
        />
        <meta property="og:url" content="https://www.cheapcabgrab.com/travelnews" />

        {/* JSON-LD structured data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: "Live Travel News",
            url: "https://www.cheapcabgrab.com/travelnews",
            description:
              "Live travel disruption alerts including roads, rail, airport and local travel updates.",
            about: [
              { "@type": "Service", name: "Heathrow Taxi", url: "https://www.cheapcabgrab.com/heathrow-taxi" },
              { "@type": "Service", name: "Minibus Hire", url: "https://www.cheapcabgrab.com/minibus-hire" },
              { "@type": "Service", name: "Coach Hire", url: "https://www.cheapcabgrab.com/coach-hire" },
              { "@type": "Service", name: "Slough Taxi", url: "https://www.cheapcabgrab.com/slough-taxi" }
            ],
          })}
        </script>
      </Helmet>

      {/* Single live strip (Heathrow-focused) */}
      <LiveStrip variant="heathrow" />

      {/* ==============================
          TOP CATEGORY SELECTOR
      =============================== */}
      <div className="tn-top-nav">
        {categories.map((c) => (
          <a
            key={c.key}
            href={`#tn-${c.key}`}
            className="tn-top-link"
            onClick={() => setOpen({ ...open, [c.key]: true })}
          >
            <span className="tn-top-icon">{c.icon}</span>
            <span className="tn-top-text">{c.title}</span>
          </a>
        ))}
      </div>

      {/* ==============================
          CONTENT SECTIONS
      =============================== */}
      {loading ? (
        <p className="tn-loading">Loading travel updates…</p>
      ) : (
        categories.map((c) => renderSection(c.key, c.icon, c.title))
      )}
    </div>
  );
}
